import { useEffect, useMemo, useRef, useState } from 'react'
import CustomModal from '../shared/CustomModal/CustomModal'
import PlotterCanvas from './PlotterCanvas'
import * as actions from '@actions'
import './PlotterCanvas.css'
import cn from 'clsx'
import { MdFileDownload, MdOutlineReportProblem, MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md'
import { COLORS, plotAndDownload, toFixed } from './canvasUtils'
import CircleSpinner from '../shared/CircleSpinner'

const PlotterModal = ({ open, modalHandler, runsheetName, runsheetId }) => {
    
    const [plots, setPlots] = useState([])
    const [activePlot, setActivePlot] = useState(null)
    const [loading, setLoading] = useState(false)
    const plotterRef = useRef(null)
    const plotProperties = useMemo(() =>
        activePlot !== null && plots[activePlot].geoJSON.metes_and_bounds.features.properties
    , [plots, activePlot])
    
    const updatePlot =  async newCoords => {
        const copy = [...plots]
        
        copy[activePlot].geoJSON.metes_and_bounds.features.geometry.coordinates = newCoords
        setPlots(copy)
        actions.updatePlotPosition(copy[activePlot].document.id, plotProperties.runsheetLegalDescriptionId, newCoords)
    }
    
    const downloadSingle = (plot, idx) => plotAndDownload(plot, COLORS[idx])
    
    const renderListItemContent = (p, idx) => {
        const hasCoordinates = !!p.geoJSON.metes_and_bounds.features.geometry.coordinates.length
        
        return (<>
            <div className="flex items-center gap-2 p-2">
                <div
                    className={cn('badge badge-lg', { 'opacity-10': p.hidden || !hasCoordinates })}
                    style={{ backgroundColor: COLORS[idx] }}>
                </div>
                
                <span className={cn('line-clamp-1 break-all',
                    { 'opacity-25': activePlot !== null && activePlot !== idx })}>
                    {p.document.documentName}
                </span>
            </div>
            {!hasCoordinates &&
                <div className="flex">
                    <span className="flex items-center justify-center w-10 h-10 text-warning tooltip tooltip-left"
                        data-tip="Couldn't extract Metes and Bounds from this document." >
                        <MdOutlineReportProblem />
                    </span>
                </div>
            }
            {hasCoordinates &&
            <div className="flex">
                <button
                    onClick={e => {
                        e.stopPropagation()
                        downloadSingle(p, idx)
                    }}
                    className="flex items-center justify-center w-10 h-10 hover:bg-base-300">
                    <MdFileDownload />
                </button>
                <button
                    onClick={e => {
                        e.stopPropagation()
                        setPlots(plots.map(plot => 
                            plot === p ? { ...plot, hidden: !plot.hidden } : plot,
                        ))
                    }}
                    className="flex items-center justify-center w-10 h-10 hover:bg-base-300">
                    {p.hidden ? <MdOutlineVisibilityOff /> : <MdOutlineVisibility />}
                </button>
            </div>}
        </>)
    }
    
    useEffect(() => {
        if (!plots.length && open && runsheetId) {
            setLoading(true)
            actions.getDocumentPlotsByRunsheet(runsheetId)
                .then(data => {
                    setPlots(data)
                    setLoading(false)
                })
        }
    }, [plots, open, runsheetId])
    
    return (
        <CustomModal
            className="max-w-screen-lg PlotterModal"
            open={open}
            modalHandler={() => modalHandler()}
            center>
            
            <div className="mb-4 modal-header">
                <h3 className="modal-title">Deed Plotter - {runsheetName}</h3>
            </div>
            
            {loading && <div className="flex flex-col gap-4 items-center justify-center h-[700px] pb-36">
                <CircleSpinner />
                <h3>Calculating Metes and Bounds</h3>
            </div>}
            
            {!loading &&
                <>
                    <div className="grid grid-cols-4">
                        {activePlot === null && <ul>
                            {plots.map((p, idx) =>
                                <li key={idx} className="plot-item" onClick={() => setActivePlot(idx)}>
                                    {renderListItemContent(p, idx)}
                                </li>)}
                        </ul>}
                        {activePlot !== null &&
                            <div>
                                <div className="bg-base-100 plot-item" onClick={() => setActivePlot(null)}>
                                    {renderListItemContent(plots[activePlot], activePlot)}
                                </div>
                                <div className="mt-2 mr-2 overflow-auto rounded shadow-lg bg-base-100">
                                    <header className="sticky left-0 p-2 text-xl border-b border-base-200">
                                        Metes and Bounds
                                    </header>
                                    {plotProperties.main_directions.map((md, i) => {
                                        return <div key={i} className="flex p-2 font-mono tracking-widest">
                                            <span className="px-1">{md}</span>
                                            <span className="px-1">{toFixed(plotProperties.augment_degrees[i], 2)
                                                .toString()
                                                .padStart(2, '0')}</span>
                                            <span className="px-1">{plotProperties.augment_directions[i]},</span>
                                            <span className="px-1">{toFixed(plotProperties.distances[i], 2)}</span>
                                            <span className="px-2">{plotProperties.unit}</span>
                                        </div>
                                    })}
                                </div>
                            </div>}
                        <div className="col-span-3 bg-base-100">
                            <PlotterCanvas
                                ref={plotterRef}
                                plots={plots}
                                updatePlot={updatePlot}
                                activePlot={activePlot}/>
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            className="btn btn-primary"
                            onClick={() => plotterRef.current.exportCanvas()}>
                            <MdFileDownload /> Export Visible Plots
                        </button>
                    
                    </div>
                </>
            }
        
        
        </CustomModal>
    )
}

export default PlotterModal
